import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';

export interface UserData {
  id: string;
  email: string;
  username?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  apps?: string; // Comma-separated list of apps the user has access to
}

interface DecodedToken {
  exp: number;
  iat: number;
  sub: string;
  email?: string;
  [key: string]: any;
}

interface AuthState {
  isAuthenticated: boolean;
  hasCompletedOnboarding: boolean;
  user: UserData | null;
  login: (userTokens: { accessToken: string; refreshToken?: string; user?: Partial<UserData> }) => void;
  checkAuth: () => boolean;
  logout: () => void;
  setUser: (user: UserData) => void;
  fetchUserProfile: () => Promise<void>;
}

const AUTH_TOKEN_KEY = 'smartai_auth_token';
const REFRESH_TOKEN_KEY = 'refreshToken';

// Utility functions
const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    return Date.now() >= decoded.exp * 1000;
  } catch {
    return true;
  }
};

const extractUserFromToken = (token: string): Partial<UserData> | null => {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    console.log('Decoded JWT token:', decoded);
    
    // Extract as much user information as possible from the token
    const userData: Partial<UserData> = {
      id: decoded.sub || decoded.userId || decoded.id,
      email: decoded.email || decoded.user_email || undefined,
      username: decoded.username || decoded.preferred_username || decoded.name || decoded.email?.split('@')[0] || undefined,
      fullName: decoded.fullName || decoded.full_name || decoded.name || 
                (decoded.firstName && decoded.lastName ? `${decoded.firstName} ${decoded.lastName}` : undefined) ||
                (decoded.given_name && decoded.family_name ? `${decoded.given_name} ${decoded.family_name}` : undefined),
      firstName: decoded.firstName || decoded.first_name || decoded.given_name || undefined,
      lastName: decoded.lastName || decoded.last_name || decoded.family_name || undefined,
    };
    
    // Clean up undefined values
    Object.keys(userData).forEach(key => {
      if (userData[key as keyof UserData] === undefined || userData[key as keyof UserData] === '') {
        delete userData[key as keyof UserData];
      }
    });
    
    console.log('Extracted user data from token:', userData);
    return userData;
  } catch (error) {
    console.error('Failed to extract user from token:', error);
    return null;
  }
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      hasCompletedOnboarding: false,
      user: null,

      login: (userTokens: { accessToken: string; refreshToken?: string; user?: Partial<UserData> }) => {
        const { accessToken, refreshToken, user: providedUser } = userTokens;

        // Store tokens
        localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
        if (refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        }

        // iOS/Safari Fix: Use first-party cookies with subdomain sharing
        // With *.creativetoolbox.ai domains, we can use sameSite: 'lax' which Safari fully supports
        const isDev = window.location.hostname === "localhost";
        
        // Store in secure cookie
        Cookies.set(AUTH_TOKEN_KEY, accessToken, {
          expires: 3, // 3 days
          secure: true,  // Always use secure in production
          sameSite: 'lax',  // Changed from 'none' - Safari compatible!
          domain: isDev ? undefined : '.creativetoolbox.ai',  // Share across all subdomains
        });

        // Extract user data from token, merge with provided user data if available
        const tokenUserData = extractUserFromToken(accessToken);
        console.log('Token user data extracted:', tokenUserData);
        console.log('Provided user data:', providedUser);
        
        const userData = providedUser ? { ...tokenUserData, ...providedUser } : tokenUserData;
        console.log('Merged user data:', userData);
        
        if (userData) {
          const fullUserData: UserData = {
            id: userData.id || 'unknown',
            email: userData.email || '',
            username: userData.username,
            fullName: userData.fullName,
            firstName: userData.firstName,
            lastName: userData.lastName,
            isActive: true,
            createdAt: userData.createdAt || new Date().toISOString(),
            lastLoginAt: new Date().toISOString(),
          };
          
          set({
            isAuthenticated: true,
            user: fullUserData,
          });
          
          console.log('User logged in with final data:', fullUserData);
        } else {
          console.log('No user data available, setting authenticated only');
          set({ isAuthenticated: true });
        }
      },

      checkAuth: (): boolean => {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        
        if (!token) {
          // Only update state if it's different
          if (get().isAuthenticated) {
            set({ isAuthenticated: false, user: null });
          }
          return false;
        }

        if (isTokenExpired(token)) {
          // Token expired, clear auth only if currently authenticated
          if (get().isAuthenticated) {
            get().logout();
          }
          return false;
        }

        // Token is valid - only update state if not already authenticated
        if (!get().isAuthenticated) {
          const userData = extractUserFromToken(token);
          if (userData) {
            set({
              isAuthenticated: true,
              user: {
                ...userData,
                isActive: true,
                createdAt: new Date().toISOString(),
              } as UserData,
            });
          } else {
            set({ isAuthenticated: true });
          }
        }

        return true;
      },

      logout: () => {
        const isDev = window.location.hostname === "localhost";
        
        // Clear localStorage
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        
        // Clear cookies with proper domain setting
        Cookies.remove(AUTH_TOKEN_KEY, {
          domain: isDev ? undefined : '.creativetoolbox.ai',
        });
        
        // Clear state
        set({
          isAuthenticated: false,
          user: null,
          hasCompletedOnboarding: false,
        });
      },

      setUser: (user: UserData) => {
        set({ user });
      },

      fetchUserProfile: async () => {
        try {
          console.log('AuthStore: Starting fetchUserProfile...');
          const currentUser = get().user;
          console.log('AuthStore: Current user before fetch:', currentUser);
          
          // Import UserService dynamically to avoid circular dependencies
          const { UserService } = await import('../services/userService');
          const profile = await UserService.fetchUserProfile();
          
          console.log('AuthStore: Profile fetched from backend:', profile);
          
          if (profile) {
            // Merge fetched profile with existing user data
            const updatedUser: UserData = {
              ...currentUser,
              ...profile,
              lastLoginAt: currentUser?.lastLoginAt || new Date().toISOString(),
            } as UserData;
            
            set({ user: updatedUser });
            console.log('AuthStore: User profile updated successfully:', updatedUser);
          } else {
            console.log('AuthStore: No profile data received from backend');
          }
        } catch (error) {
          console.error('AuthStore: Failed to fetch user profile:', error);
          
          // Log more details about the error
          if (error instanceof Error) {
            console.error('Error details:', {
              message: error.message,
              name: error.name,
              stack: error.stack,
            });
          }
          
          // If it's a network/API error, log the response details
          if ((error as any).response) {
            console.error('API Error Details:', {
              status: (error as any).response.status,
              statusText: (error as any).response.statusText,
              data: (error as any).response.data,
            });
          }
        }
      },
    }),
    {
      name: 'auth-store', // Name for localStorage
      partialize: (state) => ({
        hasCompletedOnboarding: state.hasCompletedOnboarding,
        user: state.user,
      }),
    }
  )
);

// Utility functions for debugging
export const logTokenInfo = () => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      console.log('Token info:', {
        expired: isTokenExpired(token),
        expiresAt: new Date(decoded.exp * 1000).toISOString(),
        issuedAt: new Date(decoded.iat * 1000).toISOString(),
        subject: decoded.sub,
        claims: decoded,
      });
    } catch (error) {
      console.error('Failed to decode token:', error);
    }
  } else {
    console.log('No token found');
  }
};

export const logCurrentUser = () => {
  const state = useAuthStore.getState();
  console.log('Current user state:', {
    isAuthenticated: state.isAuthenticated,
    user: state.user,
  });
  
  if (state.user) {
    console.table({
      ID: state.user.id,
      Email: state.user.email,
      Username: state.user.username,
      'Full Name': state.user.fullName,
      'First Name': state.user.firstName,
      'Last Name': state.user.lastName,
      'Last Login': state.user.lastLoginAt,
    });
  }
};

// Make debug functions available globally in development
if (typeof window !== 'undefined') {
  (window as any).debugAuth = {
    logToken: logTokenInfo,
    logUser: logCurrentUser,
    getUser: () => useAuthStore.getState().user,
    getState: () => useAuthStore.getState(),
    clearAuth: () => useAuthStore.getState().logout(),
    fetchProfile: () => useAuthStore.getState().fetchUserProfile(),
    testAPI: async () => {
      try {
        const { UserService } = await import('../services/userService');
        const profile = await UserService.fetchUserProfile();
        console.log('API Test Result:', profile);
        return profile;
      } catch (error) {
        console.error('API Test Failed:', error);
        return null;
      }
    },
  };
  console.log('Debug functions available:');
  console.log('- window.debugAuth.logToken() - Show current JWT token info');
  console.log('- window.debugAuth.logUser() - Show current user data');
  console.log('- window.debugAuth.getUser() - Get current user object');
  console.log('- window.debugAuth.getState() - Get full auth state');
  console.log('- window.debugAuth.fetchProfile() - Fetch user profile from backend');
  console.log('- window.debugAuth.testAPI() - Test API connection directly');
  console.log('- window.debugAuth.clearAuth() - Clear authentication');
}